import { CheckCircleOutlined } from '@ant-design/icons';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogTitle } from 'components/ui/dialog';
import useCheckNotificationsPermissions from 'hooks/useCheckNotificationsPermissions';
import usePreferences from 'hooks/usePreferences';
import useReminderState from 'hooks/useReminderState';
import useSchedulePeriodNotifications from 'hooks/useSchedulePeriodNotifications';
import useShouldAskForPeriod from 'hooks/useShouldAskForPeriod';
import useSymptomTrackingData from 'hooks/useSymptomTrackingData';
import ActionTypes, { NotificationIds } from 'notifications';
import { getTrackingNotificationChannelId } from 'notifications/channels';
import posthog from 'posthog-js';
import Keys from 'preferences/preferences';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Picker from 'react-mobile-picker';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from 'store/users/selectors';
import Platform from 'utils/Platform';
import NotificationSuggester from '../cards/NotificationSuggester';

export type SymptomTrackingReminderModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onReminderEnabled?: () => void;
  hidePeriodReminder?: boolean;
  hideDailyReminder?: boolean;
};

const SymptomTrackingReminderModal: React.FC<SymptomTrackingReminderModalProps> = ({
  isOpen,
  onClose,
  onReminderEnabled,
  hidePeriodReminder = false,
  hideDailyReminder = false,
}) => {
  const { t } = useTranslation();
  const currentUser = useSelector(selectCurrentUser);
  const { doesUserHavePushNotifications } = useCheckNotificationsPermissions();
  const { probableNextPeriodDates } = useSymptomTrackingData();
  const { schedulePeriodReminder, cancelPeriodReminders } = useSchedulePeriodNotifications();

  const { isPeriodTrackingEnabled } = useShouldAskForPeriod();

  const timePickerSelections: { hour: number[]; minute: number[] } = {
    hour: Array.from({ length: 24 }, (_, i) => i),
    minute: Array.from({ length: 12 }, (_, i) => i * 5),
  };

  const [timePickerValue, setTimePickerValue] = useState({ hour: 21, minute: 0 });

  const {
    reminderTime,
    isPeriodReminderEnabled,
    areDailyNotificationsEnabled,
    setReminderTime,
    clearReminderTime,
    setPeriodReminderEnabled,
    clearPeriodReminderEnabled,
  } = useReminderState();

  useEffect(() => {
    if (reminderTime) {
      setTimePickerValue(reminderTime);
    }
  }, [reminderTime]);

  const { set: setShouldAskForReminder } = usePreferences(Keys.TRACKING_SURVEY_SHOULD_ASK_FOR_REMINDER);

  const disableDailyReminder = () => {
    LocalNotifications.getPending().then(pending =>
      LocalNotifications.cancel({
        notifications: pending.notifications.filter(n => n.id === NotificationIds.SYMPTOM_TRACKING_REMINDER),
      }),
    );
    clearReminderTime();
    // The user turned the reminder off on purpose: the survey must not suggest it again.
    setShouldAskForReminder('false');

    posthog.capture('tracking.reminder_disabled');
  };

  const disablePeriodReminder = () => {
    clearPeriodReminderEnabled();
    cancelPeriodReminders();
    posthog.capture('tracking.period_reminder.disabled');
  };

  const useEnhancedNotifications =
    Platform.getPlatform() === 'ios' && posthog.getFeatureFlag('ios-notifications-better-settings') === true;

  const enableDailyReminder = () => {
    const channelId = getTrackingNotificationChannelId();
    LocalNotifications.schedule({
      notifications: [
        {
          title: t('appName'),
          body: t('symptomTracking.notification.dailyReminder', { name: currentUser?.first_name }),
          id: NotificationIds.SYMPTOM_TRACKING_REMINDER,
          schedule: { on: { hour: timePickerValue.hour, minute: timePickerValue.minute }, allowWhileIdle: true },
          actionTypeId: ActionTypes.SYMPTOM_TRACKING_REMINDER,
          ...(channelId ? { channelId } : {}),
          ...(useEnhancedNotifications ? { sound: 'default' } : {}),
        },
      ],
    });

    setReminderTime(timePickerValue);
    posthog.capture('tracking.reminder_enabled_or_changed', {
      time: timePickerValue,
    });

    if (onReminderEnabled) {
      onReminderEnabled();
    }
  };

  const enablePeriodReminder = () => {
    setPeriodReminderEnabled(true);
    if (probableNextPeriodDates) {
      schedulePeriodReminder(probableNextPeriodDates.start, areDailyNotificationsEnabled);
    }
    posthog.capture('tracking.period_reminder.enabled', { source: 'reminder_modal' });
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent aria-describedby={undefined}>
        <DialogTitle className="sr-only">
          {hideDailyReminder
            ? t('symptomTracking.reminder.periodReminder')
            : t('symptomTracking.reminder.dailyReminder')}
        </DialogTitle>
        {Platform.isNative() ? (
          <>
            {!hidePeriodReminder && isPeriodTrackingEnabled && (
              <div className="mb-8 flex flex-col items-center [&_h3]:mb-4 [&_h3_svg]:ml-0.5">
                {doesUserHavePushNotifications ? (
                  isPeriodReminderEnabled ? (
                    <>
                      <h3>
                        {t('symptomTracking.reminder.periodReminder')}{' '}
                        <span className="text-foreground">
                          activé <CheckCircleOutlined />
                        </span>
                      </h3>
                      <p className="mt-0 mb-2 text-center">{t('symptomTracking.periodReminder.description')}</p>
                      <Button size="lg" variant="destructive-subtle" onClick={() => disablePeriodReminder()}>
                        {t('symptomTracking.reminder.disable')}
                      </Button>
                    </>
                  ) : (
                    <>
                      <h3>
                        {t('symptomTracking.reminder.periodReminder')}{' '}
                        <span className="text-subtle-foreground">{t('symptomTracking.periodReminder.disabled')}</span>
                      </h3>
                      <p className="mt-0 mb-2 text-center">{t('symptomTracking.periodReminder.description')}</p>
                      <Button size="lg" onClick={() => enablePeriodReminder()}>
                        {t('symptomTracking.reminder.enable')}
                      </Button>
                    </>
                  )
                ) : (
                  <>
                    <h3>{t('symptomTracking.reminder.periodReminder')}</h3>
                    <p className="mt-0 mb-2 text-center">{t('symptomTracking.periodReminder.description')}</p>
                    <NotificationSuggester text={t('symptomTracking.reminder.enableNotifications')} />
                  </>
                )}
              </div>
            )}

            {!hideDailyReminder && (
              <>
                <div className="flex flex-col items-center">
                  <h3>{t('symptomTracking.reminder.dailyReminder')}</h3>
                  <p className="mt-0 mb-2 text-center">{t('symptomTracking.reminder.setTimeQuestion')}</p>
                  <Picker className="w-48" value={timePickerValue} onChange={setTimePickerValue}>
                    <Picker.Column key="hour" name="hour">
                      {timePickerSelections.hour.map((option: number) => (
                        <Picker.Item key={`hour-${option}`} value={option}>
                          {option.toString().padStart(2, '0')}
                        </Picker.Item>
                      ))}
                    </Picker.Column>
                    <Picker.Column key="minute" name="minute">
                      {timePickerSelections.minute.map((option: number) => (
                        <Picker.Item key={`minute-${option}`} value={option}>
                          {option.toString().padStart(2, '0')}
                        </Picker.Item>
                      ))}
                    </Picker.Column>
                  </Picker>
                </div>
                {!doesUserHavePushNotifications && (
                  <div className="flex flex-wrap items-center justify-around gap-2">
                    <NotificationSuggester text={t('symptomTracking.reminder.enableNotifications')} />
                  </div>
                )}
                <div className="flex flex-wrap items-center justify-around gap-2">
                  {areDailyNotificationsEnabled ? (
                    <Button size="lg" variant="destructive-subtle" onClick={() => disableDailyReminder()}>
                      {t('symptomTracking.reminder.disable')}
                    </Button>
                  ) : (
                    <Button size="lg" disabled={!doesUserHavePushNotifications} onClick={enableDailyReminder}>
                      {t('symptomTracking.reminder.enable')}
                    </Button>
                  )}
                  {areDailyNotificationsEnabled && (
                    <Button
                      size="lg"
                      onClick={enableDailyReminder}
                      disabled={
                        !reminderTime ||
                        (timePickerValue.hour === reminderTime.hour && timePickerValue.minute === reminderTime.minute)
                      }
                    >
                      {t('symptomTracking.reminder.change')}
                    </Button>
                  )}
                </div>
              </>
            )}
          </>
        ) : (
          <div>{t('symptomTracking.reminder.mobileOnly')}</div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SymptomTrackingReminderModal;
